import { useCallback, useState } from 'react';
import { GoogleSignInMode, useAuth } from '../../contexts/AuthContext';
import { friendlyAuthError, AuthOperation } from './authMessages';

export type AuthMode = 'signin' | 'signup';

export interface LoginViewModel {
  busy: boolean;
  mode: AuthMode;
  setMode: (mode: AuthMode) => void;
  error: string | null;
  resetMessage: string | null;
  resetPassword: (email: string) => Promise<void>;
  initialize: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, confirmPassword: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

export function useLoginViewModel(
  onSuccess: () => void,
  googleSignInMode: GoogleSignInMode = 'popup',
): LoginViewModel {
  const auth = useAuth();
  const [busy, setBusy] = useState<boolean>(true);
  const [mode, setMode] = useState<AuthMode>('signin');
  const [error, setError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const initialize = useCallback(() => {
    setBusy(true);
    // Nothing else to load up front — the AuthProvider already tracks the
    // signed-in user. We flip busy off once on mount.
    setBusy(false);
  }, []);

  const wrap = useCallback(async (
    fn: () => Promise<void>,
    operation: AuthOperation,
    options?: { completesInPlace?: boolean },
  ) => {
    setError(null);
    setResetMessage(null);
    setBusy(true);
    try {
      await fn();
      if (options?.completesInPlace !== false) {
        onSuccess();
      }
    } catch (err: any) {
      setError(friendlyAuthError(err, operation));
    } finally {
      setBusy(false);
    }
  }, [onSuccess]);

  return {
    busy,
    mode,
    setMode: (nextMode) => {
      setMode(nextMode);
      setError(null);
      setResetMessage(null);
    },
    error,
    resetMessage,
    resetPassword: (email) => wrap(async () => {
      try {
        await auth.resetPassword(email.trim());
      } catch (err: any) {
        if (err?.code !== 'auth/user-not-found') throw err;
      }
      setResetMessage(
        'If a Workbench account exists for this email, you will receive a password reset link. Check your inbox and spam folder.',
      );
    }, 'password-reset', { completesInPlace: false }),
    initialize,
    signIn: (email, password) => wrap(() => auth.signIn(email, password), 'sign-in'),
    signUp: (email, password, displayName, confirmPassword) =>
      wrap(async () => {
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }
        await auth.signUp(email, password, displayName);
      }, 'sign-up'),
    signInWithGoogle: () => wrap(
      () => auth.signInWithGoogle(googleSignInMode),
      'google',
      { completesInPlace: googleSignInMode !== 'redirect' },
    ),
  };
}
